import { Component, ViewChild, ElementRef, AfterViewInit, Renderer2, OnInit, ViewContainerRef, HostListener, Input } from '@angular/core'
import { FormType } from '../../enums/formType.enum';
import { CtFilterContainerComponent } from '../ct-filter-container/ct-filter-container.component';
import { InputBaseComponent } from '../input-base.component';
import { PfComponents } from '../../enums/pfComponents';
import { CtRegexComponent } from '../ct-regex/ct-regex.component';
import { breakpoints } from '../../environments/environment.breakpoints';
import { Subject } from 'rxjs';
import { InputType } from '../../enums/inputType.enum';

@Component({
  selector: 'ct-input-text',
  templateUrl: './ct-input-text.component.html',
  styleUrls: ['./ct-input-text.component.css'],
  host: {
    '[id]': 'getId()',
    '[hidden]': 'getHidden()',
    '[class]': "'form-group'",
    '[class.bar-filter-margin]': 'getOneLineControl()'
  }
})
export class CtInputTextComponent extends InputBaseComponent implements AfterViewInit, OnInit {

  @ViewChild('el') formControl: ElementRef;
  @ViewChild('regexTargetContainer', { read: ViewContainerRef }) regexTarget: ViewContainerRef;
  @ViewChild('inputText', { static: false }) inputTextInput: ElementRef;
  private enterPressedSource = new Subject<string>();
  public enterPressed$ = this.enterPressedSource.asObservable();

  private pattern: string;
  private patternMessage: string;
  private isPassword: boolean;
  private type: FormType;
  public typeEnum = FormType;
  private regexComponents: CtRegexComponent[] = [];
  private inputType : InputType = InputType.default;

  public isClipBoardShown: boolean = false;
  public isTooltipShown: boolean = false;
  public isFocus: boolean = false;
  public isExtraSmall: boolean = false;
  public isSmall: boolean = false;
  public isMedium: boolean = false;
  public textOrientation: string = "left";
  //constants
  public tooltipIconWidth: number = 20;
  public tooltipIconHeight: number = 20;
  public tooltipIconPath: string = 'assets/icons/tooltip.svg';
  public DomElement : ElementRef;

  public InputTypeValue = InputType;

  public regexTooltipIcons: Map<string, string> = new Map<string, string>([
    ['pending', 'assets/icons/Pending.svg'],
    ['complete', 'assets/icons/Complete.svg'],
  ]);
  renderer: any;

  //public functions
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.manageWindowResizing(window.innerWidth);
  }

  constructor() {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.manageWindowResizing(window.innerWidth);
  }

  /**
   * The ngAfterViewInit function sets the input width, initializes the value, and sets child regex
   * components.
   */
  ngAfterViewInit() {
    this.setInitValue(this.getValue());
    this.setChildRegexComponents();
    this.DomElement = this.inputTextInput.nativeElement;
  }

  /**
   * Actions required after window resizing
   * @param {number} width - number - the width of the window
   */
  private manageWindowResizing(width: number): void {
    this.isSmall = width < breakpoints.L && width >= breakpoints.M;
    this.isExtraSmall = width >= breakpoints.S && width < breakpoints.M;
    this.isMedium = width >= breakpoints.L;
  }

  /**
   * This function sets the width of an input element based on the length of its placeholder attribute.
   */
  private setInputWidth(): void {
    if (this.getIsFilterInput() && this.formControl.nativeElement.getAttribute('placeholder').length > 0) {
      const placeHolderLength: any = this.formControl.nativeElement.getAttribute('placeholder').length;
      this.formControl.nativeElement.setAttribute('size', placeHolderLength);
    }
  }

  /**
   * "If the input is valid, then if it's a filter input and not a normal input, send the text to the parent component(filter-container) to perfom the search method,
   * otherwise call original onEnter function"
   * if the @param postClean is true, that means that the 'x' button of the input text was pressed, that means that the input must be reset to its initial state
   * and also perfom the search.
   * @param {boolean} [postClean=false] - boolean = false
   * @returns The return type is void.
   */
  public onInputEnter(postClean: boolean = false): void {
    this.setIsValid();
    if (!this.getIsValid() && !postClean) return;
    if (postClean) {
      this.resetToInitState();
      this.setIsActive(false);
    }
    if (this.getIsFilterInput()) {
      let parentComponent: CtFilterContainerComponent = this.utilService.getComponentRef(this.getListenedComponentId());
      parentComponent.receiveTextFromChild(this.utilService.getComponentRef(this.getId()));
    } else {
      if(!this.getEvent('onEnter')) this.enterPressedSource.next('');
      else this.onEnter();
    }
    if (!postClean) this.setIsActive(true);
  }

  /**
   * The function toggles the state of a tooltip window.
   */
  public changeTooltipWindowState(): void {
    this.isTooltipShown = !this.isTooltipShown;
  }

  /**
   * The function takes a string input and validates it against a set of regular expression patterns
   * using a forEach loop.
   * @param {string} value - a string value that is being passed as an input to the function. It is used
   * to validate against a pattern.
   */
  public onInputChange(value: string): void {
    this.regexComponents.forEach((regexComponent: CtRegexComponent) => {
      regexComponent.validateStringAgainstPattern(value);
    });
  }

  /**
   * The function sets child regex components by retrieving metadata and adding instances of regex
   * components.
   */
  private setChildRegexComponents(): void {
    let regexComponentsMetadata: any[] = this.getMetaData().models.map((metadata: any) => {
      if (metadata.ctType === PfComponents.CtRegex) return metadata;
    }).filter((metadata: any) => metadata !== undefined);
    if (regexComponentsMetadata.length > 0) {
      this.addRegexComponentInstances(regexComponentsMetadata);
    }
  }

  /**
   * This function adds instances of a regex component to an array based on metadata.
   * @param {any[]} regexComponentsMetadata - An array of metadata objects that describe the regular
   * expression components to be added.
   */
  private addRegexComponentInstances(regexComponentsMetadata: any[]): void {
    regexComponentsMetadata.forEach((metadata: any) => {
      const messageComponentInstance = new CtRegexComponent();
      messageComponentInstance.setMetaData(metadata);
      messageComponentInstance.ngOnInit();
      this.regexComponents.push(messageComponentInstance);
    });
  }

  /**
   * This function returns a string representing a tooltip icon based on whether a regular expression is
   * valid or not.
   * @param {boolean} isValid - A boolean value indicating whether a regular expression is valid or not.
   * @returns a string which is either the value associated with the key 'complete' or 'pending' in the
   * `regexTooltipIcons` map, depending on the value of the `isValid` parameter.
   */
  public getRegexTooltipIcon(isValid: boolean): string {
    if (isValid) return this.regexTooltipIcons.get('complete');
    return this.regexTooltipIcons.get('pending');
  }

  public getTextOrientationBasedType(): string {
    if(this.getInputType() !== InputType.oversize){
      return this.getTextOrientation();
    }
    else
    return 'center'
  }

  copyToClipboard(): void{
    let text = this.getValue();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        this.changeClipboardTooltipWindowState();
      }).catch(err => {
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        this.changeClipboardTooltipWindowState();
      } catch (err) {
      }
      document.body.removeChild(textarea);
    }

  }

  public changeClipboardTooltipWindowState(): void {
    this.isClipBoardShown = !this.isClipBoardShown;
  setTimeout(() => {
        this.isClipBoardShown = !this.isClipBoardShown;
    }, 3000); 
  }

  getPattern(): string { return this.pattern }
  getPatternMessage(): string { return this.patternMessage }
  getFormControl(): any { return this.formControl }
  getIsPassword(): boolean { return this.isPassword }
  getType(): FormType { return this.type }
  getIsSummaryType() { return this.type === FormType.Summary }
  getRegexComponents(): CtRegexComponent[] { return this.regexComponents }
  getTextOrientation():string { return this.textOrientation };
  getInputType(): InputType {return this.inputType}

  setTextOrientation(textOrientation: string): void { this.textOrientation = textOrientation }
  setInputType(inputType : InputType ): void {this.inputType = inputType }
  setPattern(pattern: string): void { this.pattern = pattern }
  setPatternMessage(patternMessage: string): void { this.patternMessage = patternMessage }
  setIsPassword(isPassword: boolean): void { this.isPassword = isPassword }
  setType(type: FormType): void { this.type = type }
  setRegexComponents(regexComponents: CtRegexComponent[]): void { this.regexComponents = regexComponents }
}
